import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, X, Loader2, Copy, Trash2, Share2, Link2, FileText, Lock, Download, Eye, EyeOff, QrCode, ImagePlus } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import LinkShareOptions, { PasswordShareOptions } from "@/components/LinkShareOptions";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatBytes } from "@/lib/secureCrypto";
import { PublicShell } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { loadBranding, saveBranding, shrinkLogo, blobToDataUrl } from "@/lib/branding";
import { savedTransfers as saved, saveTransfers as persist, fetchActivity, markSeen, timeAgo } from "@/lib/transfers";

const MAX = 2 * 1024 ** 3;
const PARALLEL = 3;

async function putWithRetry(url, blob, key, tries = 4) {
  for (let i = 0; ; i++) {
    try { return await api.put(url, blob, { headers: { "Content-Type": "application/octet-stream", "X-Transfer-Key": key }, timeout: 120000 }); }
    catch (e) { if (i >= tries - 1 || e?.response?.status === 400) throw e; await new Promise((r) => setTimeout(r, 1500 * (i + 1))); }
  }
}

const copy = async (url) => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch {} };
const EXPIRIES = [1, 3, 7, 14];

const QrButton = ({ url, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors" data-testid={testId} aria-label="Show QR code">
        <QrCode className="h-3.5 w-3.5" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-4 rounded-2xl" data-testid={`${testId}-popover`}>
      <div className="bg-white p-3 rounded-xl"><QRCodeSVG value={url} size={176} level="M" /></div>
      <p className="mt-2 text-xs text-center text-muted-foreground">Scan with a phone camera</p>
    </PopoverContent>
  </Popover>
);

const shareLink = async (url) => {
  try { if (navigator.share) { await navigator.share({ title: "Files for you", url }); return; } }
  catch (e) { if (e?.name === "AbortError") return; }
  copy(url);
};

export default function SendFiles() {
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [drag, setDrag] = useState(false);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [expiry, setExpiry] = useState(7);
  const [welcome, setWelcome] = useState("");
  const [logo, setLogo] = useState(null);
  const logoRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState("");
  useEffect(() => {
    if (!logo) { setLogoUrl(""); return undefined; }
    const u = URL.createObjectURL(logo); setLogoUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [logo]);
  const [remember, setRemember] = useState(() => loadBranding().remember !== false);
  useEffect(() => {
    const b = loadBranding();
    if (b.welcome) setWelcome(b.welcome);
    if (b.logo) fetch(b.logo).then((r) => r.blob()).then(setLogo).catch(() => {});
  }, []);
  const pickLogo = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp", "image/gif"].includes(f.type)) { toast.error("Logo must be a PNG, JPG, WEBP or GIF"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Logo must be under 10 MB"); return; }
    try { setLogo(await shrinkLogo(f)); } catch { toast.error("Couldn't read that image"); }
  };
  const [activity, setActivity] = useState({});
  const inputRef = useRef(null);
  const total = files.reduce((s, f) => s + f.size, 0);

  const loadHistory = useCallback(async () => {
    const list = saved();
    const rows = await Promise.all(list.map((h) => api.get(`/public/transfers/${h.token}`)
      .then((r) => ({ ...h, ...r.data, id: h.id, key: h.key, expired: false, locked: !!r.data.locked }))
      .catch((e) => {
        const st = e?.response?.status;
        if (st === 404) return null;
        return { ...h, files: h.files || [], expired: st === 410, unreachable: st !== 410 };
      })));
    const alive = rows.filter(Boolean);
    persist(list.filter((h) => alive.some((r) => r.id === h.id)));
    setHistory(alive);
    const act = await fetchActivity().catch(() => ({}));
    setActivity(act);
    const fresh = Object.values(act).reduce((n, a) => n + a.unseen, 0);
    if (fresh) toast.success(`${fresh} new download${fresh > 1 ? "s" : ""} of files you sent`);
    markSeen(act);
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list)];
    if (next.reduce((s, f) => s + f.size, 0) > MAX) { toast.error("That's over the 2 GB limit per transfer"); return; }
    setFiles(next);
  };

  const send = async () => {
    let done = 0;
    setProgress(0); setResult(null);
    try {
      const { data: tr } = await api.post("/transfers", { message, welcome: welcome.trim(), expiry_days: expiry, ...(password.trim() ? { password: password.trim() } : {}) });
      const h = { headers: { "X-Transfer-Key": tr.manage_key } };
      if (logo) {
        try { await api.put(`/transfers/${tr.id}/logo`, logo, { headers: { "X-Transfer-Key": tr.manage_key, "Content-Type": logo.type } }); }
        catch { toast.error("Logo couldn't be added — sending without it"); }
      }
      for (const file of files) {
        const { data: fr } = await api.post(`/transfers/${tr.id}/files`, { filename: file.name, size: file.size, content_type: file.type || "application/octet-stream" }, h);
        const queue = [...Array(fr.chunks).keys()];
        const worker = async () => {
          while (queue.length) {
            const n = queue.shift();
            const blob = file.slice(n * fr.chunk_size, Math.min(file.size, (n + 1) * fr.chunk_size));
            await putWithRetry(`/transfers/${tr.id}/files/${fr.file_id}/chunks/${n}`, blob, tr.manage_key);
            done += blob.size;
            setProgress(Math.round((done / total) * 100));
          }
        };
        await Promise.all(Array.from({ length: PARALLEL }, worker));
      }
      const { data } = await api.post(`/transfers/${tr.id}/complete`, { frontend_origin: window.location.origin }, h);
      persist([{ id: tr.id, token: data.token, key: tr.manage_key, files: files.map((f) => ({ filename: f.name })), total_size: total, seen: 0 }, ...saved()]);
      setResult({ ...data, id: tr.id, key: tr.manage_key, password: password.trim(), expiry });
      try { await navigator.clipboard.writeText(data.url); } catch {}
      toast.success("Link ready — copied to clipboard");
      if (remember) saveBranding({ remember: true, welcome: welcome.trim(), logo: logo ? await blobToDataUrl(logo) : "" });
      else saveBranding({ remember: false });
      setFiles([]); setMessage(""); setPassword("");
      if (!remember) { setWelcome(""); setLogo(null); }
      loadHistory();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed — check your connection and try again");
    } finally { setProgress(null); }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this transfer? The link will stop working.")) return;
    try { await api.delete(`/transfers/${row.id}`, { headers: { "X-Transfer-Key": row.key || "" } }); }
    catch (e) { if (e?.response?.status !== 404) { toast.error("Couldn't delete — try again"); return; } }
    persist(saved().filter((x) => x.id !== row.id));
    toast.success("Transfer deleted"); loadHistory();
  };

  return (
    <PublicShell testId="send-files-page">
      <h1 className="text-4xl sm:text-5xl font-semibold font-display tracking-tight">Send big files</h1>
      <p className="mt-3 text-sm text-muted-foreground">Up to 2 GB per transfer, no sign-in needed. You get a link to send any way you like (WhatsApp, text, email…). Choose how long it lasts: 1 to 14 days.</p>

      <div className="mt-10 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            className={`w-full rounded-3xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" : "border-border hover:border-amber-400"}`}
            data-testid="send-files-dropzone"
          >
            <UploadCloud className="h-10 w-10 mx-auto text-amber-600" />
            <div className="mt-3 font-medium">Drop files here or tap to choose</div>
            <div className="text-xs text-muted-foreground mt-1">{files.length ? `${files.length} file(s) · ${formatBytes(total)} of 2 GB` : "Any file type"}</div>
          </button>
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="send-files-input" />
          {files.length > 0 && (
            <ul className="rounded-2xl border border-border divide-y divide-border max-h-64 overflow-y-auto" data-testid="send-files-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <FileText className="h-4 w-4 text-stone-400 shrink-0" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-muted-foreground">{formatBytes(f.size)}</span>
                  <button disabled={progress !== null} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800" data-testid={`send-files-remove-${i}`} aria-label="Remove"><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3" data-testid="send-files-branding">
            <button type="button" onClick={() => logoRef.current?.click()} className="h-12 w-12 shrink-0 rounded-2xl border border-dashed border-border hover:border-amber-400 overflow-hidden inline-flex items-center justify-center text-stone-400 transition-colors" data-testid="send-files-logo-btn" aria-label="Add your logo">
              {logoUrl ? <img src={logoUrl} alt="" className="h-full w-full object-cover" data-testid="send-files-logo-preview" /> : <ImagePlus className="h-5 w-5" />}
            </button>
            <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => { pickLogo(e.target.files?.[0]); e.target.value = ""; }} data-testid="send-files-logo-input" />
            <Input value={welcome} onChange={(e) => setWelcome(e.target.value)} maxLength={120} placeholder="Welcome line, e.g. Photos from Sara's wedding" data-testid="send-files-welcome" />
            {logo && <button type="button" onClick={() => { setLogo(null); if (remember) saveBranding({ ...loadBranding(), logo: "" }); }} className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800" data-testid="send-files-logo-remove" aria-label="Remove logo"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none -mt-1">
            <input type="checkbox" checked={remember} onChange={(e) => { setRemember(e.target.checked); if (!e.target.checked) saveBranding({ remember: false }); }} className="accent-amber-600 h-3.5 w-3.5" data-testid="send-files-remember-branding" />
            Remember my logo and welcome line for next time
          </label>
          <Textarea placeholder="Message shown on the download page (optional)" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} rows={4} data-testid="send-files-message" />
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
            <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password to download (optional)" className="pl-9 pr-10" maxLength={128} autoComplete="new-password" data-testid="send-files-password" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800" data-testid="send-files-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="send-files-expiry">
            <span className="text-xs text-muted-foreground mr-1">Link lasts</span>
            {EXPIRIES.map((d) => (
              <button key={d} type="button" onClick={() => setExpiry(d)} aria-pressed={expiry === d}
                className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${expiry === d ? "bg-amber-600 border-amber-600 text-white" : "border-border hover:bg-stone-100 dark:hover:bg-stone-800"}`}
                data-testid={`send-files-expiry-${d}`}>{d} day{d > 1 ? "s" : ""}</button>
            ))}
          </div>
          {progress !== null && (
            <div className="space-y-1" data-testid="send-files-progress"><Progress value={progress} /><div className="text-xs text-muted-foreground">Uploading… {progress}% — keep this page open</div></div>
          )}
          <Button onClick={send} disabled={!files.length || progress !== null} className="w-full rounded-full h-11" data-testid="send-files-submit">
            {progress !== null ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
            Get a link
          </Button>
          {result && (
            <div className="rounded-2xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-4 space-y-4" data-testid="send-files-result">
              <div>
                <div className="text-sm font-medium">Your link is ready</div>
                <div className="text-xs text-muted-foreground" data-testid="send-files-result-expiry">Expires in {result.expiry} day{result.expiry > 1 ? "s" : ""}</div>
              </div>
              <LinkShareOptions
                url={result.url}
                title="Files for you"
                testId="send-files-link"
                onEmail={async (emails) => (await api.post(`/transfers/${result.id}/email`, { emails, frontend_origin: window.location.origin }, { headers: { "X-Transfer-Key": result.key } })).data}
              />
              {result.password && <PasswordShareOptions password={result.password} testId="send-files-pw" />}
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <section className="mt-16">
          <h2 className="text-base md:text-lg font-semibold">Sent from this device</h2>
          <ul className="mt-4 divide-y divide-border rounded-2xl border border-border" data-testid="send-files-history">
            {history.map((h) => {
              const url = `${window.location.origin}/t/${h.token}`;
              return (
                <li key={h.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`transfer-row-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{(h.files || []).map((f) => f.filename).join(", ")}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                      {h.locked && <Lock className="h-3 w-3 text-amber-600" />}
                      <span>{formatBytes(h.total_size || 0)}</span>
                      {h.expired ? <span>· expired</span> : h.unreachable ? <span>· couldn't check right now</span> : <span>· expires {new Date(h.expires_at).toLocaleDateString()}</span>}
                    </div>
                    {!h.expired && !h.unreachable && (
                      <div className="text-xs mt-0.5 flex items-center gap-1.5" data-testid={`transfer-downloads-${h.id}`}>
                        <Download className="h-3 w-3" />
                        {activity[h.id]?.downloads ? `Downloaded ${activity[h.id].downloads}× · last ${timeAgo(activity[h.id].last_download_at)}` : "Not downloaded yet"}
                        {activity[h.id]?.unseen > 0 && <span className="rounded-full bg-emerald-600 text-white px-1.5 text-[10px] font-semibold" data-testid={`transfer-new-${h.id}`}>{activity[h.id].unseen} new</span>}
                      </div>
                    )}
                  </div>
                  {!h.expired && <QrButton url={url} testId={`transfer-qr-${h.id}`} />}
                  {!h.expired && <button onClick={() => shareLink(url)} className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800" data-testid={`transfer-copy-${h.id}`} aria-label="Share link"><Share2 className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => remove(h)} className="h-8 w-8 inline-flex items-center justify-center rounded-full text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/40" data-testid={`transfer-delete-${h.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </PublicShell>
  );
}
